import React, { useState } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useValidation } from '../../shared/hooks/useValidation';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { openNewTab } from '../../shared/util/openNewTab';
import { AlertPosition, AlertType, DisplayAlignment, DisplaySize, ImageStyle } from '../../shared/enum/EnumRepository';
import { PEWorkData } from '../../shared/dto/EntityRepository';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import { parseSupportedVideoUrl } from '../../shared/util/videoUrl';
import playIcon from '../../asset/icon/play.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import {
  getCompactLandscapeWidth,
  getCompactPortraitHeight,
  getDisplaySizeValue,
  PROJECT_IMAGE_SIZE
} from '../../shared/util/displayImageSize';


export interface WorkProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  data: PEWorkData;
  priority: boolean;
  imageStyle: ImageStyle;
}

const WorkVideoInPe: React.FC<WorkProps> = ({ peId, alignment, displaySize, data, priority, imageStyle }) => {
  const handleImageChange = useProjectElementListStore((s) => s.updateImage);
  const { isEditMode } = useEditMode();
  const { isValidVideoUrl } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const [isHovered, setIsHovered] = useState(false);

  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl);
  const imageRatio = data.uploadFile?.width && data.uploadFile?.height
    ? data.uploadFile.width / data.uploadFile.height
    : undefined;

  const moveToVideoUrl = () => {
    if (!data.videoUrl) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('VIDEO_URL_NULL'),
      });
      return;
    }
    if (!isValidVideoUrl(data.videoUrl)) return;
    const videoUrl = parseSupportedVideoUrl(data.videoUrl);
    if (!videoUrl) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM', { platform: videoUrl.platform }),
      callBack: () => openNewTab(videoUrl.url.href),
    });
  };

  const getStyledImage = () => {
    if (imageStyle === ImageStyle.LANDSCAPE) {
      return WorkImageForLandscape as React.ComponentType<StyledImgProps>;
    } else {
      return WorkImageForPortrait as React.ComponentType<StyledImgProps>;
    }
  };

  return (
    <ImgInPeWrapper
      $alignment={alignment || DisplayAlignment.CENTER}
      $displaySize={displaySize || DisplaySize.BIG}
      $imageStyle={imageStyle || ImageStyle.LANDSCAPE}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <ImageWrap>
        <OptimizedMoleculeImg
          srcUrl={convertedCloudFrontUrl}
          alt={data.title}
          displaySize={displaySize}
          displayAlignment={alignment || DisplayAlignment.CENTER}
          handleChange={(originUrl: string, width: number, height: number) =>
            handleImageChange(peId, originUrl, width, height)
          }
          StyledImg={getStyledImage()}
          priority={priority}
          imageRatio={imageRatio}
        />
        {!isEditMode && (
          <PlayOverlay $isHovered={isHovered}>
            <UniversalIconBtn
              size="standard"
              border={false}
              backgroundImg={playIcon}
              onClick={moveToVideoUrl}
            />
          </PlayOverlay>
        )}
      </ImageWrap>
    </ImgInPeWrapper>
  );
};

export const ImgInPeWrapper = styled.div<{ $alignment: DisplayAlignment, $displaySize: DisplaySize, $imageStyle: ImageStyle }>`
//landscape에 wide면 margin 없음

//alignment가 left면 margin-right: 1vw;
//alignment가 right면 margin-left: 1vw;
  margin: ${({ $alignment, $displaySize, $imageStyle }) => {
    // No margin if landscape and wide
    if ($imageStyle === ImageStyle.LANDSCAPE && $displaySize === DisplaySize.WIDE) {
      return '0';
    }
    // Add margin based on alignment
    if ($alignment === DisplayAlignment.LEFT_FLUSH || $alignment === DisplayAlignment.LEFT_OFFSET) {
      return '0 0.5vw 0 0';
    }
    if ($alignment === DisplayAlignment.RIGHT_FLUSH || $alignment === DisplayAlignment.RIGHT_OFFSET) {
      return '0 0 0 0.5vw';
    }
    return '0';
  }};

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 0;
`;

const ImageWrap = styled.div`
  position: relative;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PlayOverlay = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  backdrop-filter: blur(${({ $isHovered }) => ($isHovered ? 3 : 0)}px);
  transition: all 0.4s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const WorkImageForPortrait = styled.img<{ $displaySize: DisplaySize, $imageRatio?: number }>`
  height: ${({ $displaySize, $imageRatio }) =>
    getCompactPortraitHeight($displaySize, $imageRatio, PROJECT_IMAGE_SIZE)};
  width: auto;
  max-width: ${({ $displaySize }) =>
    `${getDisplaySizeValue($displaySize, PROJECT_IMAGE_SIZE.widthVw)}vw`};
  transition: height 0.6s ease, max-width 0.6s ease;
  object-fit: contain;
`;

export const WorkImageForLandscape = styled.img<{ $displaySize: DisplaySize, $imageRatio?: number }>`
  width: ${({ $displaySize, $imageRatio }) =>
    getCompactLandscapeWidth($displaySize, $imageRatio, PROJECT_IMAGE_SIZE)};
  height: auto;
  max-height: ${({ $displaySize }) =>
    `${getDisplaySizeValue($displaySize, PROJECT_IMAGE_SIZE.heightVh)}vh`};
  transition: width 0.6s ease, max-height 0.6s ease;
  object-fit: contain;
`;

export default WorkVideoInPe;
